"use client";

import { ArrowRight, ChevronLeft, ChevronRight } from "@/lib/icons";
import Image, { StaticImageData } from "next/image";
import Link from "next/link";
import { useState, useEffect, useRef, useSyncExternalStore } from "react";

const SLIDE_MS = 500;
const HOLD_MS = 1200;

const mediaStore = (query: string) => {
  let mql: MediaQueryList | null = null;
  const resolve = () => (mql ??= window.matchMedia(query));
  return {
    get: () => resolve().matches,
    getServer: () => false,
    subscribe: (onChange: () => void) => {
      const m = resolve();
      m.addEventListener("change", onChange);
      return () => m.removeEventListener("change", onChange);
    },
  };
};

const startingSlide = (title: string, count: number) => {
  if (count <= 0) return 0;
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = (hash * 31 + title.charCodeAt(i)) % count;
  }
  return hash;
};

const hoverableStore = mediaStore("(hover: hover)");
const reducedMotionStore = mediaStore("(prefers-reduced-motion: reduce)");

const WorkCard = ({
  images,
  title,
  link,
  showAll,
  description,
  technologies = [],
}: {
  images: StaticImageData[];
  title: string;
  link?: string;
  showAll?: boolean;
  description: string;
  technologies?: string[];
}) => {
  const [activeImage, setActiveImage] = useState(() =>
    startingSlide(title, images.length),
  );
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasHovered, setHasHovered] = useState(false);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  const canHover = useSyncExternalStore(
    hoverableStore.subscribe,
    hoverableStore.get,
    hoverableStore.getServer,
  );
  const prefersReducedMotion = useSyncExternalStore(
    reducedMotionStore.subscribe,
    reducedMotionStore.get,
    reducedMotionStore.getServer,
  );
  const canAutoplay = canHover && !prefersReducedMotion;

  useEffect(() => {
    const el = thumbnailsRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    const observer = new ResizeObserver(update);
    observer.observe(el);
    el.addEventListener("scroll", update, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", update);
    };
  }, [images.length]);

  // One interval for the whole hover — `activeImage` is deliberately not a
  // dependency, so advancing doesn't tear down and restack the timer. The
  // modulo is what wraps the last image back to the first.
  useEffect(() => {
    if (!isHovered || !canAutoplay || images.length < 2) return;
    const id = setInterval(() => {
      setActiveImage((current) => (current + 1) % images.length);
    }, SLIDE_MS + HOLD_MS);
    return () => clearInterval(id);
  }, [isHovered, canAutoplay, images.length]);

  const scrollThumbnails = (direction: -1 | 1) => {
    thumbnailsRef.current?.scrollBy({
      left: direction * 136,
      behavior: "smooth",
    });
  };

  // Slides only become real <Image>s once the card has been hovered; before
  // that they are the static import's blurDataURL, which is already in the
  // bundle. Keeping the active slide always mounted is what makes thumbnail
  // taps work on devices that never hover.
  const track = (
    <div
      className={`flex w-full h-full ${hasHovered ? "transition-transform duration-500 ease-in-out" : ""}`}
      style={{ transform: `translateX(-${activeImage * 100}%)` }}
    >
      {images.map((image, i) => (
        <div
          key={i}
          className="work-slide"
          aria-hidden={i !== activeImage}
        >
          {hasHovered || i === activeImage ? (
            <Image
              src={image}
              loading="lazy"
              placeholder="blur"
              title={title}
              alt={`Work Image of ${title}`}
              width={528}
              height={297}
              className={`aspect-[17/10] w-full min-w-72 h-full object-cover object-center transition-all ease duration-300 ${link ? "shadow-md group-hover:scale-105" : ""}`}
              sizes="(max-width: 640px) 100vw,(max-width: 1024px) 50vw, 33vw"
            />
          ) : (
            <div
              className="work-slide-bg"
              style={{ backgroundImage: `url(${image.blurDataURL})` }}
            />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={`work-card w-full h-full pointer-events-auto p-3 md:p-4 col-span-1 ${showAll ? "" : "sm:first:col-span-2 md:last:hidden lg:last:block"} md:first:col-span-1`}
    >
      <div
        className="relative group w-full h-full max-h-64 lg:max-h-48 rounded-xl border-[0.5px] border-light/5 overflow-hidden"
        onMouseEnter={() => {
          if (!canAutoplay) return;
          setHasHovered(true);
          setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        {link ? (
          <Link
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            title={"Go to Website: " + link}
            className="block w-full h-full"
          >
            {track}
          </Link>
        ) : (
          track
        )}
        {link && (
          <div className="absolute top-1 group-hover:animate-vibrate right-1 w-8 h-8 overflow-hidden border-[0.5px] flex-center border-light/5 rounded-full bg-dark">
            <Link
              href={link}
              aria-label={"Go to Website: " + link}
              rel="noopener noreferrer"
              target="_blank"
              className="flex-center hover:scale-110 transition-all duration-200 ease-in-out"
            >
              <ArrowRight className="text-accent text-sm group-hover:animate-link" />
            </Link>
          </div>
        )}
        <div className="text-xs absolute font-mono left-1 bottom-1 px-2 py-1 bg-dark/50 text-light/60 rounded-lg">
          {activeImage + 1}/{images.length}
        </div>
      </div>
      <div className="mt-1 w-full max-w-full shrink-0 flex items-center gap-1 overflow-hidden">
        <button
          className="work-nav-btn"
          onClick={() => scrollThumbnails(-1)}
          disabled={!canScrollLeft}
          name="Previous images"
          aria-label="Previous images"
        >
          <ChevronLeft className="text-sm" />
        </button>
        <div
          ref={thumbnailsRef}
          className="flex-1 min-w-0 flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {images.map((image, i) => (
            <button
              key={i}
              className="work-thumb"
              onClick={() => setActiveImage(i)}
              name={`Select Image ${i + 1}`}
              aria-label={`Select Image ${i + 1}`}
            >
              <Image
                src={image}
                loading="lazy"
                placeholder="blur"
                title={title}
                alt={`Work Image of ${title} (${i + 1})`}
                width={64}
                height={36}
                draggable="false"
                className={`work-thumb-img ${activeImage === i ? "" : "opacity-35 hover:opacity-75"}`}
              />
            </button>
          ))}
        </div>
        <button
          className="work-nav-btn"
          onClick={() => scrollThumbnails(1)}
          disabled={!canScrollRight}
          name="Next images"
          aria-label="Next images"
        >
          <ChevronRight className="text-sm" />
        </button>
      </div>
      <h3 className="mt-1.5 text-lg text-start font-mono font-bold">{title}</h3>
      <p
        className={`text-xs text-light/60 font-mono text-start ${technologies.length > 0 ? "" : "pb-4"}`}
      >
        {description}
      </p>
      {technologies.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2 pb-4">
          {technologies.map((tech) => (
            <span
              key={tech}
              className="tech-chip"
            >
              {tech}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkCard;
